import type { PuzzleDefinition } from '@/types';
import { PathwayType } from '@/types';

// ═══════════════════════════════════════════════════════════════════════════
// Chapter 6: "Cascade" — Tier 6-7
// Complex puzzles using all pathway types. Each puzzle uses fully disjoint
// clusters to ensure predictable BFS with no inter-cluster interference.
// ═══════════════════════════════════════════════════════════════════════════

// ── ch6-01 "Mirror Maze" — Tier 6, 8 nodes, 10 pw, 2 strikes (PASSED)
export const ch6_01: PuzzleDefinition = {
  id: 'ch6-01', version: 1, tier: 6,
  metadata: { title: 'Mirror Maze', category: 'harmonic', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.2, y: 0.3, frequency: 4, locked: false },
      { id: 'B', x: 0.05, y: 0.6, frequency: 3, locked: false },
      { id: 'C', x: 0.35, y: 0.1, frequency: 12, locked: false },
      { id: 'D', x: 0.7, y: 0.3, frequency: 4, locked: false },
      { id: 'E', x: 0.35, y: 0.6, frequency: 7, locked: false },
      { id: 'F', x: 0.55, y: 0.6, frequency: 3, locked: false },
      { id: 'G', x: 0.85, y: 0.1, frequency: 12, locked: false },
      { id: 'H', x: 0.85, y: 0.6, frequency: 7, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p3', source: 'A', target: 'E', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p4', source: 'B', target: 'E', type: PathwayType.Amplifying, harmonic: 'M3', directed: false },
      { id: 'p5', source: 'C', target: 'E', type: PathwayType.Inverting, harmonic: 'm3', directed: false },
      { id: 'p6', source: 'D', target: 'F', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p7', source: 'D', target: 'G', type: PathwayType.Inverting, harmonic: 'P5', directed: true },
      { id: 'p8', source: 'F', target: 'G', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p9', source: 'G', target: 'H', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p10', source: 'D', target: 'H', type: PathwayType.Filtering, harmonic: 'M3', directed: false, filterThreshold: 3 },
    ],
  },
  constraints: { strikeBudget: 3, minimumStrikes: 2, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'D'], optimal: true }],
};

// ── ch6-02 "Crossroads" — Tier 6, 8 nodes, 10 pw, 2 strikes (PASSED)
export const ch6_02: PuzzleDefinition = {
  id: 'ch6-02', version: 1, tier: 6,
  metadata: { title: 'Crossroads', category: 'harmonic', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.2, y: 0.3, frequency: 3, locked: false },
      { id: 'B', x: 0.05, y: 0.6, frequency: 9, locked: false },
      { id: 'C', x: 0.35, y: 0.6, frequency: 5, locked: false },
      { id: 'D', x: 0.2, y: 0.9, frequency: 2, locked: false },
      { id: 'E', x: 0.7, y: 0.3, frequency: 3, locked: false },
      { id: 'F', x: 0.55, y: 0.6, frequency: 9, locked: false },
      { id: 'G', x: 0.85, y: 0.6, frequency: 5, locked: false },
      { id: 'H', x: 0.7, y: 0.9, frequency: 2, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'B', target: 'C', type: PathwayType.Inverting, harmonic: 'M3', directed: false },
      { id: 'p5', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p6', source: 'E', target: 'F', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p7', source: 'E', target: 'G', type: PathwayType.Clear, harmonic: 'm3', directed: false },
      { id: 'p8', source: 'E', target: 'H', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p9', source: 'F', target: 'G', type: PathwayType.Inverting, harmonic: 'M3', directed: false },
      { id: 'p10', source: 'G', target: 'H', type: PathwayType.Clear, harmonic: 'm3', directed: false },
    ],
  },
  constraints: { strikeBudget: 3, minimumStrikes: 2, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'E'], optimal: true }],
};

// ── ch6-03 "Inverted Spectrum" — Tier 6, 8 nodes, 8 pw, 2 strikes
// Two fully disjoint clusters, no cross-links.
// Left: A(6)→B(Damp+0)=6, C(Inv-1)=2-1=1, D(Amp+2)=8+2=10
// Right: E(6)→F(Damp+0)=6, G(Inv-1)=2-1=1, H(Amp+2)=8+2=10
// Solved: A=6,B=6,C=1,D=10, E=6,F=6,G=1,H=10
//   A-B unison✓ A-C P4:(6-1)=5✓ A-D M3:(6-10+12)%12=8→inv=4✓ B-D M3:(6-10+12)=8→inv=4✓
//   E-F unison✓ E-G P4:(6-1)=5✓ E-H M3:(6-10+12)=8→inv=4✓ F-H M3:(6-10+12)=8→inv=4✓
export const ch6_03: PuzzleDefinition = {
  id: 'ch6-03', version: 1, tier: 6,
  metadata: { title: 'Inverted Spectrum', category: 'harmonic', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.2, y: 0.25, frequency: 6, locked: false },
      { id: 'B', x: 0.05, y: 0.55, frequency: 6, locked: false },
      { id: 'C', x: 0.35, y: 0.1, frequency: 2, locked: false },
      { id: 'D', x: 0.35, y: 0.55, frequency: 8, locked: false },
      { id: 'E', x: 0.7, y: 0.25, frequency: 6, locked: false },
      { id: 'F', x: 0.55, y: 0.55, frequency: 6, locked: false },
      { id: 'G', x: 0.85, y: 0.1, frequency: 2, locked: false },
      { id: 'H', x: 0.85, y: 0.55, frequency: 8, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Inverting, harmonic: 'P4', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Amplifying, harmonic: 'M3', directed: false },
      { id: 'p4', source: 'B', target: 'D', type: PathwayType.Filtering, harmonic: 'M3', directed: false, filterThreshold: 5 },
      { id: 'p5', source: 'E', target: 'F', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p6', source: 'E', target: 'G', type: PathwayType.Inverting, harmonic: 'P4', directed: false },
      { id: 'p7', source: 'E', target: 'H', type: PathwayType.Amplifying, harmonic: 'M3', directed: false },
      { id: 'p8', source: 'F', target: 'H', type: PathwayType.Filtering, harmonic: 'M3', directed: false, filterThreshold: 5 },
    ],
  },
  constraints: { strikeBudget: 3, minimumStrikes: 2, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'E'], optimal: true }],
};

// ── ch6-04 "Tidal Lock" — Tier 6, 8 nodes, 10 pw, 2 strikes (PASSED)
export const ch6_04: PuzzleDefinition = {
  id: 'ch6-04', version: 1, tier: 6,
  metadata: { title: 'Tidal Lock', category: 'flow', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.15, y: 0.2, frequency: 7, locked: false },
      { id: 'B', x: 0.35, y: 0.2, frequency: 6, locked: false },
      { id: 'C', x: 0.35, y: 0.55, frequency: 11, locked: false },
      { id: 'D', x: 0.15, y: 0.55, frequency: 5, locked: false },
      { id: 'E', x: 0.65, y: 0.2, frequency: 7, locked: false },
      { id: 'F', x: 0.85, y: 0.2, frequency: 6, locked: false },
      { id: 'G', x: 0.85, y: 0.55, frequency: 11, locked: false },
      { id: 'H', x: 0.65, y: 0.55, frequency: 5, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Inverting, harmonic: 'P4', directed: true },
      { id: 'p3', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'P4', directed: false },
      { id: 'p4', source: 'A', target: 'D', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p5', source: 'A', target: 'C', type: PathwayType.Filtering, harmonic: 'P4', directed: false, filterThreshold: 5 },
      { id: 'p6', source: 'E', target: 'F', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p7', source: 'F', target: 'G', type: PathwayType.Inverting, harmonic: 'P4', directed: true },
      { id: 'p8', source: 'G', target: 'H', type: PathwayType.Clear, harmonic: 'P4', directed: false },
      { id: 'p9', source: 'E', target: 'H', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p10', source: 'E', target: 'G', type: PathwayType.Filtering, harmonic: 'P4', directed: false, filterThreshold: 5 },
    ],
  },
  constraints: { strikeBudget: 3, minimumStrikes: 2, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'E'], optimal: true }],
};

// ── ch6-05 "Signal Split" — Tier 6, 8 nodes, 8 pw, 2 strikes (PASSED)
export const ch6_05: PuzzleDefinition = {
  id: 'ch6-05', version: 1, tier: 6,
  metadata: { title: 'Signal Split', category: 'cascade', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.2, y: 0.35, frequency: 2, locked: false },
      { id: 'B', x: 0.05, y: 0.65, frequency: 7, locked: false },
      { id: 'C', x: 0.35, y: 0.65, frequency: 5, locked: false },
      { id: 'D', x: 0.2, y: 0.9, frequency: 2, locked: false },
      { id: 'E', x: 0.7, y: 0.35, frequency: 2, locked: false },
      { id: 'F', x: 0.55, y: 0.65, frequency: 7, locked: false },
      { id: 'G', x: 0.85, y: 0.65, frequency: 7, locked: false },
      { id: 'H', x: 0.7, y: 0.9, frequency: 1, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'B', target: 'D', type: PathwayType.Clear, harmonic: 'P5', directed: false },
      { id: 'p5', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'M3', directed: false },
      { id: 'p6', source: 'E', target: 'F', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p7', source: 'E', target: 'G', type: PathwayType.Inverting, harmonic: 'M3', directed: false },
      { id: 'p8', source: 'E', target: 'H', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: { strikeBudget: 3, minimumStrikes: 2, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'E'], optimal: true }],
};

// ── ch6-06 "Pressure Valve" — Tier 6, 8 nodes, 8 pw, 2 strikes
// Two fully disjoint clusters.
// Left: A(9)→B(Amp+2)=7+2=9, C(Inv-1)=3-1=2, D(Clear+1)=8+1=9
// Right: E(9)→F(Amp+2)=7+2=9, G(Inv-1)=3-1=2, H(Clear+1)=8+1=9
// Solved: A=9,B=9,C=2,D=9, E=9,F=9,G=2,H=9
//   A-B unison✓ A-C P5:(9-2)=7✓ A-D unison✓ C-D P5:(2-9+12)=5→inv=7✓
//   E-F unison✓ E-G P5:(9-2)=7✓ E-H unison✓ G-H P5:(2-9+12)=5→inv=7✓
export const ch6_06: PuzzleDefinition = {
  id: 'ch6-06', version: 1, tier: 6,
  metadata: { title: 'Pressure Valve', category: 'constraint', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.25, y: 0.3, frequency: 9, locked: false },
      { id: 'B', x: 0.1, y: 0.6, frequency: 7, locked: false },
      { id: 'C', x: 0.4, y: 0.1, frequency: 3, locked: false },
      { id: 'D', x: 0.4, y: 0.6, frequency: 8, locked: false },
      { id: 'E', x: 0.7, y: 0.3, frequency: 9, locked: false },
      { id: 'F', x: 0.55, y: 0.6, frequency: 7, locked: false },
      { id: 'G', x: 0.85, y: 0.1, frequency: 3, locked: false },
      { id: 'H', x: 0.85, y: 0.6, frequency: 8, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p3', source: 'A', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p4', source: 'C', target: 'D', type: PathwayType.Filtering, harmonic: 'P5', directed: false, filterThreshold: 5 },
      { id: 'p5', source: 'E', target: 'F', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p6', source: 'E', target: 'G', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p7', source: 'E', target: 'H', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p8', source: 'G', target: 'H', type: PathwayType.Filtering, harmonic: 'P5', directed: false, filterThreshold: 5 },
    ],
  },
  constraints: { strikeBudget: 3, minimumStrikes: 2, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'E'], optimal: true }],
};

// ── ch6-07 "Parallel Lines" — Tier 6, 8 nodes, 8 pw, 2 strikes
// Two directed chains, fully disjoint.
// Left: A→B→C→D. Right: E→F→G→H.
// Strike A: B(+1)=12+1=1, C(+2)=11+2=1, D(+3)=10+3=1.
// Strike E: F(+1)=7+1=8, G(+2)=6+2=8, H(+3)=5+3=8.
// Solved: A=1,B=1,C=1,D=1 (unison). E=8,F=8,G=8,H=8 (unison).
// Intra-cluster cross: B-D Filtering(10) for harmony only (B.f=12<10 blocks propagation).
// E-H: Inverting for harmony (E.f=8→same ring, no effect since E-H are in same chain at different rings).
// Actually E-H undirected puts H at ring1 from E as well as ring3 from E→F→G→H.
// ring1<ring3. H at ring1 via E-H Inv, delta=1*(-1)=-1. H=5+(-1)=4. ✗
// Fix: make E-H directed E→H so BFS goes E→H and H at ring1 with Inv delta=-1.
// Then H=5-1=4. Need H=8. ✗. Drop E-H cross-link. Use simple chain only.
// Replace with B-C Amp unison (intra-cluster, same ring as B→C at ring2, but B→C directed
// makes C reachable from B at ring2; B-C undirected would also be ring2).
// Actually let's just have pure chains with no cross-links.
export const ch6_07: PuzzleDefinition = {
  id: 'ch6-07', version: 1, tier: 6,
  metadata: { title: 'Parallel Lines', category: 'flow', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.1, y: 0.2, frequency: 1, locked: false },
      { id: 'B', x: 0.3, y: 0.2, frequency: 12, locked: false },
      { id: 'C', x: 0.5, y: 0.2, frequency: 11, locked: false },
      { id: 'D', x: 0.7, y: 0.2, frequency: 10, locked: false },
      { id: 'E', x: 0.1, y: 0.7, frequency: 8, locked: false },
      { id: 'F', x: 0.3, y: 0.7, frequency: 7, locked: false },
      { id: 'G', x: 0.5, y: 0.7, frequency: 6, locked: false },
      { id: 'H', x: 0.7, y: 0.7, frequency: 5, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p3', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p4', source: 'C', target: 'D', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 13 },
      { id: 'p5', source: 'E', target: 'F', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p6', source: 'F', target: 'G', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p7', source: 'G', target: 'H', type: PathwayType.Clear, harmonic: 'unison', directed: true },
      { id: 'p8', source: 'G', target: 'H', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 13 },
    ],
  },
  constraints: { strikeBudget: 3, minimumStrikes: 2, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'E'], optimal: true }],
};

// ── ch6-08 "Harmonic Web" — Tier 7, 9 nodes, 9 pw, 3 strikes
// Three fully disjoint hub-spoke clusters.
// A(4)→B(Clear+1)=3+1=4, C(Inv-1)=12-1=11. B-C at ring2 with stacking? No, pure hub.
// D(4)→E(Clear+1)=3+1=4, F(Amp+2)=9+2=11.
// G(4)→H(Clear+1)=3+1=4, I(Damp+0)=4.
// Solved: A=4,B=4,C=11, D=4,E=4,F=11, G=4,H=4,I=4
//   A-B unison✓ A-C P5:(4-11+12)=5→inv=7✓ B-C P5:(4-11+12)=5→inv=7✓
//   D-E unison✓ D-F P5:(4-11+12)=5→inv=7✓ E-F P5✓
//   G-H unison✓ G-I unison✓ H-I unison✓
export const ch6_08: PuzzleDefinition = {
  id: 'ch6-08', version: 1, tier: 7,
  metadata: { title: 'Harmonic Web', category: 'composition', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.15, y: 0.25, frequency: 4, locked: false },
      { id: 'B', x: 0.05, y: 0.6, frequency: 3, locked: false },
      { id: 'C', x: 0.3, y: 0.6, frequency: 12, locked: false },
      { id: 'D', x: 0.5, y: 0.25, frequency: 4, locked: false },
      { id: 'E', x: 0.4, y: 0.6, frequency: 3, locked: false },
      { id: 'F', x: 0.65, y: 0.6, frequency: 9, locked: false },
      { id: 'G', x: 0.85, y: 0.25, frequency: 4, locked: false },
      { id: 'H', x: 0.75, y: 0.6, frequency: 3, locked: false },
      { id: 'I', x: 0.95, y: 0.6, frequency: 4, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p3', source: 'B', target: 'C', type: PathwayType.Filtering, harmonic: 'P5', directed: false, filterThreshold: 10 },
      { id: 'p4', source: 'D', target: 'E', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p5', source: 'D', target: 'F', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p6', source: 'E', target: 'F', type: PathwayType.Filtering, harmonic: 'P5', directed: false, filterThreshold: 10 },
      { id: 'p7', source: 'G', target: 'H', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p8', source: 'G', target: 'I', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p9', source: 'H', target: 'I', type: PathwayType.Clear, harmonic: 'unison', directed: true },
    ],
  },
  constraints: { strikeBudget: 4, minimumStrikes: 3, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'D', 'G'], optimal: true }],
};

// ── ch6-09 "Phase Shift" — Tier 7, 9 nodes, 9 pw, 3 strikes
// Three fully disjoint hub-spoke clusters.
// A(7)→B(Clear+1)=11+1=12, C(Amp+2)=5+2=7. B-C at ring2 inside cluster? B,C at ring1.
// D(7)→E(Clear+1)=11+1=12, F(Inv-1)=8-1=7.
// G(7)→H(Clear+1)=11+1=12, I(Damp+0)=7.
// Solved: A=7,B=12,C=7, D=7,E=12,F=7, G=7,H=12,I=7
//   A-B P4:(7-12+12)=7→inv=5✓ A-C unison✓ B-C P4:(12-7)=5✓
//   D-E P4:(7-12+12)=7→inv=5✓ D-F unison✓ E-F P4:(12-7)=5✓
//   G-H P4:(7-12+12)=7→inv=5✓ G-I unison✓ H-I P4:(12-7)=5✓
export const ch6_09: PuzzleDefinition = {
  id: 'ch6-09', version: 1, tier: 7,
  metadata: { title: 'Phase Shift', category: 'composition', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.15, y: 0.2, frequency: 7, locked: false },
      { id: 'B', x: 0.05, y: 0.55, frequency: 11, locked: false },
      { id: 'C', x: 0.3, y: 0.55, frequency: 5, locked: false },
      { id: 'D', x: 0.5, y: 0.2, frequency: 7, locked: false },
      { id: 'E', x: 0.4, y: 0.55, frequency: 11, locked: false },
      { id: 'F', x: 0.6, y: 0.55, frequency: 8, locked: false },
      { id: 'G', x: 0.8, y: 0.2, frequency: 7, locked: false },
      { id: 'H', x: 0.7, y: 0.55, frequency: 11, locked: false },
      { id: 'I', x: 0.9, y: 0.55, frequency: 7, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'P4', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Amplifying, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'B', target: 'C', type: PathwayType.Filtering, harmonic: 'P4', directed: false, filterThreshold: 11 },
      { id: 'p4', source: 'D', target: 'E', type: PathwayType.Clear, harmonic: 'P4', directed: false },
      { id: 'p5', source: 'D', target: 'F', type: PathwayType.Inverting, harmonic: 'unison', directed: false },
      { id: 'p6', source: 'E', target: 'F', type: PathwayType.Filtering, harmonic: 'P4', directed: false, filterThreshold: 11 },
      { id: 'p7', source: 'G', target: 'H', type: PathwayType.Clear, harmonic: 'P4', directed: false },
      { id: 'p8', source: 'G', target: 'I', type: PathwayType.Dampening, harmonic: 'unison', directed: false },
      { id: 'p9', source: 'H', target: 'I', type: PathwayType.Clear, harmonic: 'P4', directed: true },
    ],
  },
  constraints: { strikeBudget: 4, minimumStrikes: 3, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'D', 'G'], optimal: true }],
};

// ── ch6-10 "Cascade Mastery" — Tier 7, 9 nodes, 9 pw, 3 strikes
// Three fully disjoint hub-spoke clusters.
// A(10)→B(Amp+2)=1+2=3, C(Inv-1)=4-1=3.
// D(10)→E(Clear+1)=9+1=10, F(Amp+2)=1+2=3.
// G(10)→H(Clear+1)=9+1=10, I(Inv-1)=4-1=3.
// Solved: A=10,B=3,C=3, D=10,E=10,F=3, G=10,H=10,I=3
//   A-B P5:(10-3)=7✓ A-C P5:(10-3)=7✓ B-C unison:(3-3)=0✓
//   D-E unison✓ D-F P5:(10-3)=7✓ E-F P5:(10-3)=7✓
//   G-H unison✓ G-I P5:(10-3)=7✓ H-I P5:(10-3)=7✓
export const ch6_10: PuzzleDefinition = {
  id: 'ch6-10', version: 1, tier: 7,
  metadata: { title: 'Cascade Mastery', category: 'composition', author: 'resonance-team', created: '2026-03-01' },
  board: {
    nodes: [
      { id: 'A', x: 0.1, y: 0.2, frequency: 10, locked: false },
      { id: 'B', x: 0.05, y: 0.55, frequency: 1, locked: false },
      { id: 'C', x: 0.25, y: 0.55, frequency: 4, locked: false },
      { id: 'D', x: 0.45, y: 0.2, frequency: 10, locked: false },
      { id: 'E', x: 0.35, y: 0.55, frequency: 9, locked: false },
      { id: 'F', x: 0.55, y: 0.55, frequency: 1, locked: false },
      { id: 'G', x: 0.75, y: 0.2, frequency: 10, locked: false },
      { id: 'H', x: 0.65, y: 0.55, frequency: 9, locked: false },
      { id: 'I', x: 0.85, y: 0.55, frequency: 4, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p3', source: 'B', target: 'C', type: PathwayType.Filtering, harmonic: 'unison', directed: false, filterThreshold: 8 },
      { id: 'p4', source: 'D', target: 'E', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p5', source: 'D', target: 'F', type: PathwayType.Amplifying, harmonic: 'P5', directed: false },
      { id: 'p6', source: 'E', target: 'F', type: PathwayType.Filtering, harmonic: 'P5', directed: false, filterThreshold: 8 },
      { id: 'p7', source: 'G', target: 'H', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p8', source: 'G', target: 'I', type: PathwayType.Inverting, harmonic: 'P5', directed: false },
      { id: 'p9', source: 'H', target: 'I', type: PathwayType.Clear, harmonic: 'P5', directed: true },
    ],
  },
  constraints: { strikeBudget: 4, minimumStrikes: 3, maxPropagationRings: 3 },
  solutions: [{ strikes: ['A', 'D', 'G'], optimal: true }],
};

export const chapter6Puzzles: PuzzleDefinition[] = [
  ch6_01, ch6_02, ch6_03, ch6_04, ch6_05,
  ch6_06, ch6_07, ch6_08, ch6_09, ch6_10,
];
